import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { FlaskConical, RefreshCw, Beaker, Sparkles, Filter } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function InnovationLab({ onAnalyze }) {
  const [themes, setThemes] = useState([]);
  const [picks, setPicks] = useState([]);
  const [activeTheme, setActiveTheme] = useState("all");
  const [minScore, setMinScore] = useState(50);
  const [loading, setLoading] = useState(false);
  const [scannedAt, setScannedAt] = useState(null);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    axios.get(`${API}/api/innovation-lab/themes`).then((r) => setThemes(r.data.themes || []));
  }, []);

  const fetchPicks = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ min_score: minScore, limit: 60 });
      if (activeTheme && activeTheme !== "all") params.set("theme", activeTheme);
      const r = await axios.get(`${API}/api/innovation-lab/scan?${params}`);
      setPicks(r.data.picks || []);
      setScannedAt(r.data.scanned_at);
    } catch (e) {
      console.error("innovation lab scan failed", e);
    } finally {
      setLoading(false);
    }
  }, [activeTheme, minScore]);

  useEffect(() => { fetchPicks(); }, [fetchPicks]);

  const scoreColor = (s) => {
    if (s >= 80) return "from-emerald-500 to-teal-400 text-emerald-950";
    if (s >= 60) return "from-amber-500 to-yellow-400 text-amber-950";
    if (s >= 40) return "from-sky-500 to-blue-400 text-sky-950";
    return "from-slate-600 to-slate-500 text-slate-100";
  };

  return (
    <div className="space-y-6" data-testid="innovation-lab-tab">
      {/* HEADER */}
      <div className="bg-gradient-to-br from-emerald-900/40 via-teal-900/30 to-slate-900/60 border border-emerald-700/40 rounded-2xl p-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h2 className="text-2xl font-bold flex items-center gap-3 mb-2">
              <FlaskConical className="w-7 h-7 text-emerald-400" />
              INNOVATION LAB — Vraies Pépites
            </h2>
            <p className="text-sm text-slate-400 max-w-2xl">
              Détecte les boîtes avec techno propriétaire + marché en explosion + croissance réelle.
              ~125 tickers curated sur 12 thèmes (clean energy, biotech, quantum, space, GLP-1, defense, etc.)
              Scoring sur thèse fondamentale, pas signaux Wall Street.
            </p>
          </div>
          <button
            data-testid="innovation-refresh-btn"
            onClick={fetchPicks}
            disabled={loading}
            className="bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white font-semibold px-4 py-2 rounded-lg flex items-center gap-2 shrink-0"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            Scanner
          </button>
        </div>

        {/* THEME FILTER PILLS */}
        <div className="flex flex-wrap gap-1.5 mt-5">
          <button
            data-testid="innovation-theme-all"
            onClick={() => setActiveTheme("all")}
            className={`text-xs px-3 py-1.5 rounded-full border transition-all ${
              activeTheme === "all"
                ? "bg-emerald-500/30 border-emerald-500 text-emerald-200"
                : "bg-slate-800/50 border-slate-700 text-slate-400 hover:border-emerald-700"
            }`}
          >
            ✨ Tous ({themes.reduce((s, t) => s + (t.count || 0), 0)})
          </button>
          {themes.map((t) => (
            <button
              key={t.key}
              data-testid={`innovation-theme-${t.key}`}
              onClick={() => setActiveTheme(t.key)}
              className={`text-xs px-3 py-1.5 rounded-full border transition-all ${
                activeTheme === t.key
                  ? "bg-emerald-500/30 border-emerald-500 text-emerald-200"
                  : "bg-slate-800/50 border-slate-700 text-slate-400 hover:border-emerald-700"
              }`}
            >
              {t.icon} {t.label} <span className="text-slate-500">({t.count})</span>
            </button>
          ))}
        </div>

        <div className="flex items-center gap-4 mt-4">
          <div className="flex items-center gap-2 text-xs text-slate-400">
            <Filter className="w-3.5 h-3.5" /> Score min
          </div>
          <input
            data-testid="innovation-min-score"
            type="range"
            min={20}
            max={90}
            step={5}
            value={minScore}
            onChange={(e) => setMinScore(parseInt(e.target.value))}
            className="flex-1 max-w-xs accent-emerald-500"
          />
          <div className="text-xs text-emerald-300 font-bold">≥ {minScore}</div>
        </div>

        {scannedAt && (
          <div className="text-xs text-slate-500 mt-3">
            Dernier scan : {new Date(scannedAt).toLocaleString("fr-FR")} · {picks.length} pépite{picks.length > 1 ? "s" : ""}
          </div>
        )}
      </div>

      {/* PICKS GRID */}
      {loading && picks.length === 0 && (
        <div className="text-center text-slate-500 py-12">
          <Beaker className="w-8 h-8 mx-auto animate-pulse mb-3 text-emerald-400" />
          Analyse de 125 tickers innovation...
        </div>
      )}

      {!loading && picks.length === 0 && (
        <div className="text-center text-slate-500 py-12 border border-slate-800 rounded-xl">
          Aucune pépite ne franchit le seuil de {minScore}. Baissez ou changez de thème.
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {picks.map((p) => {
          const isExpanded = expanded === p.ticker + p.theme;
          return (
            <div
              key={p.ticker + p.theme}
              data-testid={`innovation-pick-${p.ticker}`}
              className="bg-slate-900/70 border border-slate-800 hover:border-emerald-700/60 rounded-xl p-4 transition-all"
            >
              <div className="flex items-start gap-3">
                <div className={`text-xl font-black bg-gradient-to-br ${scoreColor(p.innovation_score)} rounded-xl px-3 py-1.5 shadow-lg min-w-[56px] text-center`}>
                  {p.innovation_score}
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <button
                      data-testid={`innovation-analyze-${p.ticker}`}
                      onClick={() => onAnalyze && onAnalyze(p.ticker)}
                      className="text-lg font-bold text-white hover:text-emerald-300"
                    >
                      {p.ticker}
                    </button>
                    <span className="text-xs text-slate-400 truncate max-w-[180px]">{p.name}</span>
                    <span className="text-[10px] bg-emerald-900/40 text-emerald-300 px-1.5 py-0.5 rounded-full">
                      {p.theme_icon} {p.theme_label}
                    </span>
                    {p.market_cap_m && (
                      <span className="text-[10px] bg-slate-800 text-slate-400 px-1.5 py-0.5 rounded-full">
                        ${(p.market_cap_m / 1000).toFixed(1)}B
                      </span>
                    )}
                  </div>

                  <p className="text-sm text-slate-300 leading-snug mb-2 italic">"{p.thesis}"</p>

                  <div className="grid grid-cols-4 gap-2 text-xs mb-2">
                    <div>
                      <div className="text-slate-500 text-[9px]">Prix</div>
                      <div className="font-semibold">${(p.price || 0).toFixed(2)}</div>
                    </div>
                    {p.rev_growth_pct !== null && p.rev_growth_pct !== undefined && (
                      <div>
                        <div className="text-slate-500 text-[9px]">Rev YoY</div>
                        <div className={`font-semibold ${p.rev_growth_pct > 0 ? "text-emerald-300" : "text-red-300"}`}>
                          {p.rev_growth_pct > 0 ? "+" : ""}{p.rev_growth_pct.toFixed(0)}%
                        </div>
                      </div>
                    )}
                    {p.runway_months !== null && p.runway_months !== undefined && (
                      <div>
                        <div className="text-slate-500 text-[9px]">Runway</div>
                        <div className={`font-semibold ${p.runway_months >= 24 ? "text-emerald-300" : p.runway_months >= 12 ? "text-amber-300" : "text-red-300"}`}>
                          {p.runway_months.toFixed(0)}mo
                        </div>
                      </div>
                    )}
                    {p.upside_pct !== null && p.upside_pct !== undefined && (
                      <div>
                        <div className="text-slate-500 text-[9px]">Upside</div>
                        <div className={`font-semibold ${p.upside_pct > 0 ? "text-sky-300" : "text-slate-400"}`}>
                          {p.upside_pct > 0 ? "+" : ""}{p.upside_pct.toFixed(0)}%
                        </div>
                      </div>
                    )}
                  </div>

                  <button
                    data-testid={`innovation-toggle-${p.ticker}`}
                    onClick={() => setExpanded(isExpanded ? null : p.ticker + p.theme)}
                    className="text-[10px] text-emerald-400 hover:text-emerald-300"
                  >
                    {isExpanded ? "▲ Cacher signaux" : "▼ Voir tous les signaux"}
                  </button>

                  {isExpanded && (
                    <ul className="mt-2 space-y-0.5 text-xs">
                      {p.reasons.map((r, i) => (
                        <li key={i} className="text-slate-300">{r}</li>
                      ))}
                      {p.n_analysts !== null && p.n_analysts !== undefined && (
                        <li className="text-slate-400 text-[11px] mt-1">
                          Analystes : {p.n_analysts} · Reco : {p.recommendation || "n/a"}
                        </li>
                      )}
                    </ul>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
